import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';

import { resolveSummary, unaddressableView, type WebsiteCardView } from '../website/card-summary';
import { adCardCopy } from './ad-card-copy';
import { useBrandSettled } from '../website/useBrandSettled';
import { AD_CARD_READS, type AdCardRow } from './ad-card-reads';
import { deriveAdCardSummary, type AdCardInput } from './ad-card-summary';
import { marketingAreaTitle } from './area-copy';
import type { MarketingAreaId } from './areas';

/** What the read returned (null when it failed), stamped with the brand it was made for. */
type Read = { tenantId: number; rows: AdCardRow[] | null };

const isActive = (job: CreativeJob | undefined): boolean => job?.status === 'queued' || job?.status === 'running';

/** The ids of the rows' runs still in flight, as one comparable string. Pure. */
const activeRuns = (rows: readonly AdCardRow[], jobsById: Record<string, CreativeJob>): string =>
  rows
    .flatMap((row) => (row.jobId && isActive(jobsById[row.jobId]) ? [row.jobId] : []))
    .sort()
    .join(',');

/**
 * A Marketing area's hub card: reads the area's library once per brand (a read of a superseded brand is aborted and
 * dropped) and again whenever one of its runs finishes, so a freshly rendered poster replaces the old one; the
 * "Generating" chip comes from the jobs already live in the store.
 */
export const useAdAreaCard = (area: MarketingAreaId, path: string): WebsiteCardView => {
  const { t } = useTranslation();
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const brandSettled = useBrandSettled();
  const tenantRaw = Number(displayBrand?.tenant_id);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const jobsById = useAppSelector(selectCreativeJobsMap);

  const [read, setRead] = React.useState<Read | null>(null);
  // Bumped when a run of this area finishes, to read the library again.
  const [reread, setReread] = React.useState(0);

  React.useEffect(() => {
    if (tenantId === undefined) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    AD_CARD_READS[area](tenantId, signal).then(
      (rows) => {
        if (!signal.aborted) setRead({ tenantId, rows });
      },
      () => {
        if (!signal.aborted) setRead({ tenantId, rows: null });
      },
    );
    return () => controller.abort();
  }, [area, tenantId, reread]);

  // Until this brand's read lands, the card shows it as loading, never the previous brand's.
  const rows = read !== null && read.tenantId === tenantId ? read.rows : undefined;
  const running = rows ? activeRuns(rows, jobsById) : '';
  const runningRef = React.useRef<{ tenantId: number | undefined; running: string }>({ tenantId, running });
  React.useEffect(() => {
    const before = runningRef.current;
    runningRef.current = { tenantId, running };
    if (before.tenantId !== tenantId || before.running === '') return;
    const now = new Set(running.split(','));
    if (before.running.split(',').some((id) => !now.has(id))) setReread((n) => n + 1);
  }, [tenantId, running]);

  if (tenantId === undefined) {
    return unaddressableView(brandSettled, { label: marketingAreaTitle(t, area), to: path });
  }
  const input: AdCardInput =
    rows === undefined
      ? { status: 'loading' }
      : rows === null
        ? { status: 'failed' }
        : {
            status: 'ready',
            items: rows.map((row) => ({
              state: row.jobId && isActive(jobsById[row.jobId]) ? 'generating' : row.generated ? 'ready' : 'draft',
              poster: row.poster,
            })),
          };
  return resolveSummary(
    deriveAdCardSummary(input),
    (c) => adCardCopy(t, area, c),
    () => undefined,
  );
};
